import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { FALLBACK_SITE_SERVICES } from "../../hooks/useSiteServices";
import { AdminField, inputCls } from "./AdminLayout";
import { apiErrorMessage } from "../../lib/apiErrors";

function linesToItems(text) {
  return text.split("\n").map((s) => s.trim()).filter(Boolean);
}

function itemsToLines(items) {
  return (items || []).join("\n");
}

function flattenOfferingItems(offering) {
  const all = [];
  (offering.groups || []).forEach((g) => all.push(...(g.items || [])));
  return all;
}

function offeringFromForm({ icon, title, summary, itemsText }) {
  return {
    icon: icon || "Sparkles",
    title,
    summary,
    groups: [{ title: null, items: linesToItems(itemsText) }],
  };
}

function applySitePayload(setters, d) {
  const source = d || FALLBACK_SITE_SERVICES;
  setters.setIndustriesText(
    itemsToLines(source.industries?.length ? source.industries : FALLBACK_SITE_SERVICES.industries)
  );
  setters.setHomeCards(
    JSON.parse(JSON.stringify(source.home_cards?.length ? source.home_cards : FALLBACK_SITE_SERVICES.home_cards))
  );
  setters.setOfferings(
    (source.offerings?.length ? source.offerings : FALLBACK_SITE_SERVICES.offerings).map((o) => ({
      icon: o.icon,
      title: o.title,
      summary: o.summary,
      itemsText: itemsToLines(flattenOfferingItems(o)),
    }))
  );
  setters.setWhyChoose(
    JSON.parse(JSON.stringify(source.why_choose?.length ? source.why_choose : FALLBACK_SITE_SERVICES.why_choose))
  );
  setters.setPackages(
    JSON.parse(JSON.stringify(source.packages?.length ? source.packages : FALLBACK_SITE_SERVICES.packages))
  );
}

export default function AdminServices() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [industriesText, setIndustriesText] = useState("");
  const [homeCards, setHomeCards] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [whyChoose, setWhyChoose] = useState([]);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    setLoading(true);
    const setters = { setIndustriesText, setHomeCards, setOfferings, setWhyChoose, setPackages };
    api
      .get("/site/services")
      .then((r) => applySitePayload(setters, r.data))
      .catch((err) => {
        applySitePayload(setters, FALLBACK_SITE_SERVICES);
        toast.error(apiErrorMessage(err, "Could not load site content from server. Showing defaults — save after fixing the backend."));
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        industries: linesToItems(industriesText),
        home_cards: homeCards,
        offerings: offerings.map(offeringFromForm),
        why_choose: whyChoose,
        packages: packages.map((p) => ({
          name: p.name,
          rate: Number(p.rate) || 0,
          oldRate: p.oldRate ? Number(p.oldRate) : null,
          tag: p.tag || "",
          features: linesToItems(p.featuresText ?? itemsToLines(p.features)),
        })),
      };
      await api.put("/site/services", payload);
      toast.success("Site content saved. Public pages will update on refresh.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <form onSubmit={save} className="space-y-10">
      <div>
        <h1 className="font-serif-display text-4xl">Services & pricing</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Controls Home service cards, Services page, package rates, and the calculator.
        </p>
      </div>

      <section className="rounded-2xl border border-border p-6 space-y-4">
        <h2 className="font-serif-display text-2xl">Package rates</h2>
        <p className="text-sm text-muted-foreground">Package names must stay Basic, Modern, Premium, Luxury for the calculator.</p>
        {packages.map((p, i) => (
          <div key={p.name} className="grid sm:grid-cols-2 gap-3 p-4 rounded-xl bg-secondary/30 border border-border">
            <AdminField label={`${p.name} — rate (₹/sq.ft)`}>
              <input
                type="number"
                className={inputCls}
                value={p.rate}
                onChange={(e) => {
                  const next = [...packages];
                  next[i] = { ...next[i], rate: e.target.value };
                  setPackages(next);
                }}
              />
            </AdminField>
            <AdminField label="Tag line">
              <input
                className={inputCls}
                value={p.tag || ""}
                onChange={(e) => {
                  const next = [...packages];
                  next[i] = { ...next[i], tag: e.target.value };
                  setPackages(next);
                }}
              />
            </AdminField>
            <AdminField label="Strikethrough old rate (optional)">
              <input
                type="number"
                className={inputCls}
                value={p.oldRate ?? ""}
                onChange={(e) => {
                  const next = [...packages];
                  next[i] = { ...next[i], oldRate: e.target.value ? Number(e.target.value) : null };
                  setPackages(next);
                }}
              />
            </AdminField>
            <div className="sm:col-span-2">
              <AdminField label="Features (one per line)">
                <textarea
                  rows={4}
                  className={`${inputCls} resize-none`}
                  value={p.featuresText ?? itemsToLines(p.features)}
                  onChange={(e) => {
                    const next = [...packages];
                    next[i] = { ...next[i], featuresText: e.target.value };
                    setPackages(next);
                  }}
                />
              </AdminField>
            </div>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-border p-6 space-y-4">
        <h2 className="font-serif-display text-2xl">Home page — service cards</h2>
        {homeCards.map((c, i) => (
          <div key={i} className="grid sm:grid-cols-3 gap-3 p-4 rounded-xl bg-secondary/30 border border-border">
            <AdminField label="Lucide icon name">
              <input
                className={inputCls}
                value={c.icon}
                onChange={(e) => {
                  const next = [...homeCards];
                  next[i] = { ...next[i], icon: e.target.value };
                  setHomeCards(next);
                }}
              />
            </AdminField>
            <AdminField label="Title">
              <input
                className={inputCls}
                value={c.title}
                onChange={(e) => {
                  const next = [...homeCards];
                  next[i] = { ...next[i], title: e.target.value };
                  setHomeCards(next);
                }}
              />
            </AdminField>
            <AdminField label="Description">
              <input
                className={inputCls}
                value={c.desc}
                onChange={(e) => {
                  const next = [...homeCards];
                  next[i] = { ...next[i], desc: e.target.value };
                  setHomeCards(next);
                }}
              />
            </AdminField>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-border p-6 space-y-6">
        <h2 className="font-serif-display text-2xl">Services page — offerings</h2>
        {offerings.map((o, i) => (
          <div key={i} className="space-y-3 p-4 rounded-xl bg-secondary/30 border border-border">
            <div className="grid sm:grid-cols-2 gap-3">
              <AdminField label="Icon (Lucide name)">
                <input
                  className={inputCls}
                  value={o.icon}
                  onChange={(e) => {
                    const next = [...offerings];
                    next[i] = { ...next[i], icon: e.target.value };
                    setOfferings(next);
                  }}
                />
              </AdminField>
              <AdminField label="Title">
                <input
                  className={inputCls}
                  value={o.title}
                  onChange={(e) => {
                    const next = [...offerings];
                    next[i] = { ...next[i], title: e.target.value };
                    setOfferings(next);
                  }}
                />
              </AdminField>
            </div>
            <AdminField label="Summary">
              <textarea
                rows={2}
                className={`${inputCls} resize-none`}
                value={o.summary}
                onChange={(e) => {
                  const next = [...offerings];
                  next[i] = { ...next[i], summary: e.target.value };
                  setOfferings(next);
                }}
              />
            </AdminField>
            <AdminField label="Service items (one per line)">
              <textarea
                rows={5}
                className={`${inputCls} resize-none font-mono text-xs`}
                value={o.itemsText}
                onChange={(e) => {
                  const next = [...offerings];
                  next[i] = { ...next[i], itemsText: e.target.value };
                  setOfferings(next);
                }}
              />
            </AdminField>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-border p-6 space-y-4">
        <h2 className="font-serif-display text-2xl">Industries we serve</h2>
        <AdminField label="One industry per line">
          <textarea
            rows={8}
            className={`${inputCls} resize-none`}
            value={industriesText}
            onChange={(e) => setIndustriesText(e.target.value)}
          />
        </AdminField>
      </section>

      <section className="rounded-2xl border border-border p-6 space-y-4">
        <h2 className="font-serif-display text-2xl">Why choose Kalpavriksha</h2>
        {whyChoose.map((w, i) => (
          <div key={i} className="grid sm:grid-cols-3 gap-3 p-4 rounded-xl bg-secondary/30 border border-border">
            <AdminField label="Icon">
              <input
                className={inputCls}
                value={w.icon}
                onChange={(e) => {
                  const next = [...whyChoose];
                  next[i] = { ...next[i], icon: e.target.value };
                  setWhyChoose(next);
                }}
              />
            </AdminField>
            <AdminField label="Title">
              <input
                className={inputCls}
                value={w.title}
                onChange={(e) => {
                  const next = [...whyChoose];
                  next[i] = { ...next[i], title: e.target.value };
                  setWhyChoose(next);
                }}
              />
            </AdminField>
            <AdminField label="Description">
              <input
                className={inputCls}
                value={w.desc}
                onChange={(e) => {
                  const next = [...whyChoose];
                  next[i] = { ...next[i], desc: e.target.value };
                  setWhyChoose(next);
                }}
              />
            </AdminField>
          </div>
        ))}
      </section>

      <button
        type="submit"
        disabled={saving}
        className="rounded-full bg-primary text-primary-foreground px-8 py-3.5 font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save all site content"}
      </button>
    </form>
  );
}
